import type { Avatar } from "@storyline/shared";
import { useCallback, useEffect, useState } from "react";
import { HttpError } from "@/src/common/services/api-error";
import { deleteAvatar, fetchAvatars, uploadAvatarBinary } from "@/src/settings/services/admin-avatars-http.service";

const GENERIC_ERROR = "Une erreur est survenue. Veuillez réessayer.";

const toMessage = (caught: unknown): string => (caught instanceof HttpError ? caught.message : GENERIC_ERROR);

export const useAvatarCatalog = () => {
	const [avatars, setAvatars] = useState<Avatar[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isUploading, setIsUploading] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const reload = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await fetchAvatars();
			setAvatars(data.avatars);
			setHasError(false);
		} catch {
			setHasError(true);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void reload();
	}, [reload]);

	const upload = useCallback(async (file: File): Promise<void> => {
		setError(null);
		setIsUploading(true);
		try {
			const avatar = await uploadAvatarBinary(file);
			setAvatars((previous) => [avatar, ...previous]);
		} catch (caught) {
			setError(toMessage(caught));
		} finally {
			setIsUploading(false);
		}
	}, []);

	const remove = useCallback(async (id: string): Promise<void> => {
		setError(null);
		try {
			await deleteAvatar(id);
			setAvatars((previous) => previous.filter((avatar) => avatar.id !== id));
		} catch (caught) {
			setError(toMessage(caught));
		}
	}, []);

	return { avatars, isLoading, isUploading, hasError, error, upload, remove, reload };
};
